import { DialogComponent } from '@theme/dialog';
import { checkPincode, normalizeDigits, getVerifiedPincode, storeVerifiedPincode, clearVerifiedPincode } from '@theme/pincode-gate';

const SUCCESS_CLOSE_DELAY = 700;

/**
 * @typedef {object} PincodeGateDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} message
 *
 * @extends {DialogComponent<PincodeGateDialogRefs>}
 */
class PincodeGateDialog extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'input', 'submitButton', 'message'];

  /** @type {(() => void) | null} */
  #onValidated = null;

  connectedCallback() {
    super.connectedCallback();

    this.messages = {
      empty: this.dataset.msgEmpty || 'Please enter pincode.',
      invalid: this.dataset.msgInvalid || 'Please enter a valid 6-digit pincode.',
      success: this.dataset.msgSuccess || 'Delivery is available in your area.',
      unserviceable: this.dataset.msgUnavailable || 'Delivery is not available in your area.',
    };

    this.refs.form.addEventListener('submit', this.#handleSubmit);
    this.refs.input.addEventListener('input', this.#handleInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form.removeEventListener('submit', this.#handleSubmit);
    this.refs.input.removeEventListener('input', this.#handleInput);
  }

  /**
   * Opens the dialog for a fresh pincode check.
   *
   * @param {{ onValidated?: () => void }} [options]
   */
  open({ onValidated } = {}) {
    this.#onValidated = onValidated ?? null;
    this.refs.form.reset();
    this.#clearMessage();
    this.showDialog();
    requestAnimationFrame(() => this.refs.input.focus());
  }

  #handleInput = () => {
    const { input } = this.refs;
    const digits = normalizeDigits(input.value);
    if (digits !== input.value) input.value = digits;

    if (this.refs.message.textContent) this.#clearMessage();
  };

  #handleSubmit = (event) => {
    event.preventDefault();

    const { input } = this.refs;
    const result = checkPincode(input.value);

    if (!result.ok) {
      this.#setMessage('error', this.messages[result.reason ?? 'invalid']);
      input.setAttribute('aria-invalid', 'true');
      input.focus();

      // A genuinely wrong/different pincode (not just an empty submit) means
      // any earlier verified pincode may not be the one this shopper actually
      // has anymore - clear it and broadcast so every gated surface on the
      // site re-locks instead of continuing to trust that stale success.
      if (result.reason !== 'empty') {
        clearVerifiedPincode();
        document.dispatchEvent(new CustomEvent('pincode-gate:verified', { detail: { pincode: null } }));
      }
      return;
    }

    input.removeAttribute('aria-invalid');
    this.#setMessage('success', this.messages.success);
    const pincode = input.value.trim();
    storeVerifiedPincode(pincode);

    // Broadcast so every gated add-to-cart surface already on the page (other
    // product cards, cart-drawer upsell buttons) unlocks immediately instead
    // of only the one card whose "Check Availability" click opened this
    // dialog - each surface caches nothing and just re-reads localStorage
    // when it hears this.
    document.dispatchEvent(new CustomEvent('pincode-gate:verified', { detail: { pincode } }));

    const onValidated = this.#onValidated;
    this.#onValidated = null;

    setTimeout(() => {
      this.closeDialog();
      onValidated?.();
    }, SUCCESS_CLOSE_DELAY);
  };

  /**
   * @param {'error' | 'success'} state
   * @param {string} text
   */
  #setMessage(state, text) {
    this.refs.message.textContent = text;
    this.refs.message.setAttribute('data-state', state);
  }

  #clearMessage() {
    this.refs.message.textContent = '';
    this.refs.message.removeAttribute('data-state');
    this.refs.input.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('pincode-gate-dialog')) {
  customElements.define('pincode-gate-dialog', PincodeGateDialog);
}

// custom-product-card.js and cart-drawer-upsell.js are classic (non-module)
// scripts and can't `import` from @theme/pincode-gate directly, so this is
// the bridge they use to check whether the popup already verified a pincode.
window.PincodeGate = { getVerifiedPincode };
